<template>
  <div class="help-center-page">
    <!-- Hero Banner -->
    <section class="hero-banner">
      <div class="hero-content">
        <p class="hero-label">帮助中心</p>
        <h1 class="hero-title">有什么可以帮您的？</h1>
        <div class="search-box">
          <input
            v-model="searchQuery"
            type="text"
            placeholder="搜索帮助文档，如：如何购买产品、部署教程..."
            class="search-input"
          />
          <button class="search-btn">
            <Icon icon="lucide:search" :size="20" color="#fff" />
          </button>
        </div>
      </div>
    </section>

    <!-- Main Content -->
    <div class="page-container">
      <!-- Category Cards -->
      <div class="category-grid">
        <div
          v-for="cat in helpCategories"
          :key="cat.id"
          class="category-card"
          :class="{ active: activeTab === cat.id }"
          @click="switchTab(cat.id)"
        >
          <div class="cat-icon-wrapper" :style="{ background: cat.bgColor }">
            <Icon :icon="cat.icon" :size="26" :color="cat.color" />
          </div>
          <h3 class="cat-name">{{ cat.name }}</h3>
          <p class="cat-desc">{{ cat.desc }}</p>
        </div>
      </div>

      <!-- Tab Content Area -->
      <div class="tab-content-wrapper">
        <!-- Quickstart Tab -->
        <div v-if="activeTab === 'quickstart'" class="tab-content">
          <h3 class="tab-section-title">快速入门指南</h3>
          <div class="quickstart-card">
            <div v-for="step in helpQuickstart" :key="step.step" class="quickstart-step">
              <div class="step-number">{{ step.step }}</div>
              <div class="step-content">
                <h4 class="step-title">{{ step.title }}</h4>
                <p class="step-desc">{{ step.desc }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- FAQ Tab -->
        <div v-if="activeTab === 'faq'" class="tab-content">
          <h3 class="tab-section-title">常见问题</h3>
          <div class="faq-list">
            <div
              v-for="faq in displayFaqItems"
              :key="faq.id"
              class="faq-item"
              :class="{ open: openFaq === faq.id }"
            >
              <div class="faq-header" @click="toggleFaq(faq.id)">
                <h4 class="faq-question">{{ faq.question }}</h4>
                <Icon
                  icon="lucide:chevron-down"
                  :size="18"
                  class="faq-chevron"
                  :class="{ rotated: openFaq === faq.id }"
                />
              </div>
              <div v-if="openFaq === faq.id" class="faq-answer">
                <p>{{ faq.answer }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Videos Tab -->
        <div v-if="activeTab === 'video'" class="tab-content">
          <h3 class="tab-section-title">视频教程</h3>
          <div class="video-grid">
            <div
              v-for="video in helpVideos"
              :key="video.title"
              class="video-card"
              @click="playVideo(video)"
            >
              <div class="video-thumb" :style="{ background: video.gradient }">
                <div class="play-btn">
                  <Icon icon="lucide:play" :size="28" color="#fff" />
                </div>
                <span class="video-duration">{{ video.duration }}</span>
              </div>
              <div class="video-info">
                <h4 class="video-title">{{ video.title }}</h4>
                <p class="video-desc">{{ video.desc }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- API Docs Tab -->
        <div v-if="activeTab === 'api'" class="tab-content">
          <h3 class="tab-section-title">API文档</h3>
          <div class="api-list">
            <div v-for="api in helpApis" :key="api.endpoint" class="api-item">
              <div class="api-header">
                <span class="api-method" :class="`method-${api.method.toLowerCase()}`">
                  {{ api.method }}
                </span>
                <code class="api-endpoint">{{ api.endpoint }}</code>
              </div>
              <p class="api-desc">{{ api.desc }}</p>
              <div class="api-code-block">
                <pre>{{ api.example }}</pre>
              </div>
            </div>
          </div>
          <div class="api-cta">
            <Icon icon="lucide:book-open" :size="24" color="#FF9500" />
            <p class="api-cta-title">需要完整的API文档？</p>
            <p class="api-cta-sub"> 访问开发者中心获取完整API参考、SDK下载和示例代码 </p>
            <button class="api-cta-btn" @click="goToDevCenter"> 前往开发者中心 </button>
          </div>
        </div>
      </div>

      <!-- Document Categories -->
      <section class="doc-categories-section">
        <h2 class="section-title">按产品查找文档</h2>
        <div class="doc-cat-grid">
          <div
            v-for="doc in helpDocCategories"
            :key="doc.name"
            class="doc-cat-card"
            @click="goToProduct(doc.productId)"
          >
            <div class="doc-icon-wrapper" :style="{ background: doc.bgColor }">
              <Icon :icon="doc.icon" :size="20" :color="doc.color" />
            </div>
            <div class="doc-info">
              <p class="doc-name">{{ doc.name }}</p>
              <p class="doc-count">{{ doc.docCount }}</p>
            </div>
          </div>
        </div>
      </section>

      <!-- Still Need Help? CTA -->
      <section class="help-cta-section">
        <h2 class="cta-title">还没找到答案？</h2>
        <p class="cta-subtitle"> 提交工单或联系我们的技术支持团队，5分钟内响应 </p>
        <div class="cta-actions">
          <button class="cta-btn-primary" @click="goToSupport">
            <Icon icon="lucide:ticket" :size="18" /> 提交工单
          </button>
          <button class="cta-btn-outline" @click="showTrialModal">
            <Icon icon="lucide:phone" :size="18" /> 在线咨询
          </button>
        </div>
        <div class="cta-contact">
          <div class="contact-item">
            <Icon icon="lucide:phone-call" :size="24" />
            <p class="contact-value">400-646-5288</p>
            <p class="contact-label">服务热线 9:00-18:00</p>
          </div>
          <div class="contact-item">
            <Icon icon="lucide:mail" :size="24" />
            <p class="contact-value">kefu@zjsai.com</p>
            <p class="contact-label">技术支持邮箱</p>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import { Icon } from '@/components/Icon'
import {
  helpCategories,
  helpQuickstart,
  faqItems,
  helpVideos,
  helpApis,
  helpDocCategories
} from '../data/portalData'
import { usePortalState } from '../usePortalState'
import { getPortalFaqs } from '@/api/portal'

defineOptions({ name: 'HelpCenterPage' })

const router = useRouter()
const { showTrialModal } = usePortalState()

const searchQuery = ref('')
const activeTab = ref<string>('quickstart')
const openFaq = ref<string>('')
const displayFaqItems = ref(faqItems)

onMounted(async () => {
  try {
    const list = await getPortalFaqs()
    if (Array.isArray(list) && list.length) {
      displayFaqItems.value = list.map((item) => ({
        id: String(item.id),
        question: item.question,
        answer: item.answer || ''
      }))
    }
  } catch {
    // CMS 未配置或暂不可用时保留当前门户内容。
  }
})

function switchTab(tabId: string) {
  activeTab.value = tabId
  openFaq.value = ''
}

function toggleFaq(faqId: string) {
  openFaq.value = openFaq.value === faqId ? '' : faqId
}

function playVideo(video: (typeof helpVideos)[0]) {
  ElMessage.success(`视频播放中：${video.title}`)
}

function goToDevCenter() {
  ElMessage.success('即将跳转到开发者中心')
}

function goToProduct(productId: string) {
  router.push(`/portal/products?product=${productId}`)
}

function goToSupport() {
  router.push('/portal/support')
}
</script>

<style lang="scss" scoped>
.help-center-page {
  // Hero Banner
  .hero-banner {
    height: 280px;
    background: linear-gradient(135deg, #001b5e, #0066ff);
    position: relative;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
  }

  .hero-content {
    position: relative;
    z-index: 2;
    color: #fff;
    max-width: 720px;
    padding: 0 24px;
  }

  .hero-label {
    font-size: 14px;
    opacity: 0.7;
    letter-spacing: 4px;
    margin-bottom: 12px;
  }

  .hero-title {
    font-size: 40px;
    font-weight: 900;
    margin: 0 0 24px;
  }

  .search-box {
    display: flex;
    gap: 0;
    max-width: 560px;
    margin: 0 auto;
    background: #fff;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  }

  .search-input {
    flex: 1;
    height: 48px;
    padding: 0 20px;
    border: none;
    font-size: 15px;
    outline: none;
  }

  .search-btn {
    width: 56px;
    background: #0066ff;
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background 0.3s ease;

    &:hover {
      background: #0044cc;
    }
  }

  // Page Container
  .page-container {
    max-width: 1280px;
    margin: 0 auto;
    padding: 48px 24px;
  }

  // Category Grid
  .category-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;
    margin-bottom: 48px;

    @media (max-width: 1024px) {
      grid-template-columns: repeat(2, 1fr);
    }

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
  }

  .category-card {
    border: 1px solid #e5e7eb;
    border-radius: 16px;
    padding: 28px 20px;
    text-align: center;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
      transform: translateY(-4px);
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
    }

    &.active {
      border-color: #0066ff;
      box-shadow: 0 4px 16px rgba(0, 102, 255, 0.15);
    }
  }

  .cat-icon-wrapper {
    width: 56px;
    height: 56px;
    border-radius: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 16px;
  }

  .cat-name {
    font-size: 16px;
    font-weight: 700;
    margin: 0 0 6px;
    color: #111827;
  }

  .cat-desc {
    font-size: 13px;
    color: #6b7280;
    margin: 0;
  }

  // Tab Content
  .tab-content-wrapper {
    max-width: 900px;
    margin: 0 auto;
  }

  .tab-content {
    animation: fadeIn 0.3s ease;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(8px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .tab-section-title {
    font-size: 20px;
    font-weight: 700;
    margin: 0 0 20px;
    color: #111827;
  }

  // Quickstart
  .quickstart-card {
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    padding: 32px;
  }

  .quickstart-step {
    display: flex;
    gap: 16px;
    align-items: flex-start;
    margin-bottom: 24px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .step-number {
    width: 36px;
    height: 36px;
    background: #0066ff;
    color: #fff;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    flex-shrink: 0;
  }

  .step-title {
    font-weight: 600;
    margin: 0 0 4px;
    color: #111827;
  }

  .step-desc {
    font-size: 14px;
    color: #6b7280;
    margin: 0;
  }

  // FAQ
  .faq-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .faq-item {
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    overflow: hidden;
  }

  .faq-header {
    padding: 20px 24px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    cursor: pointer;
    transition: background 0.2s ease;

    &:hover {
      background: #f9fafb;
    }
  }

  .faq-question {
    font-weight: 600;
    font-size: 15px;
    margin: 0;
    color: #111827;
  }

  .faq-chevron {
    flex-shrink: 0;
    transition: transform 0.3s ease;
    color: #9ca3af;

    &.rotated {
      transform: rotate(180deg);
    }
  }

  .faq-answer {
    padding: 0 24px 20px;

    p {
      font-size: 14px;
      color: #6b7280;
      margin: 0;
      line-height: 1.7;
    }
  }

  // Video Grid
  .video-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
  }

  .video-card {
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    overflow: hidden;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
    }
  }

  .video-thumb {
    height: 180px;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
  }

  .play-btn {
    width: 60px;
    height: 60px;
    background: rgba(255, 255, 255, 0.2);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: transform 0.3s ease;

    .video-card:hover & {
      transform: scale(1.1);
    }
  }

  .video-duration {
    position: absolute;
    bottom: 12px;
    right: 16px;
    color: #fff;
    font-size: 12px;
    background: rgba(0, 0, 0, 0.4);
    padding: 2px 8px;
    border-radius: 4px;
  }

  .video-info {
    padding: 16px;
  }

  .video-title {
    font-weight: 600;
    margin: 0 0 4px;
    color: #111827;
  }

  .video-desc {
    font-size: 13px;
    color: #6b7280;
    margin: 0;
  }

  // API Docs
  .api-item {
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    padding: 24px;
    margin-bottom: 16px;

    &:last-of-type {
      margin-bottom: 0;
    }

    @media (max-width: 768px) {
      padding: 16px;
    }
  }

  .api-header {
    display: flex;
    gap: 12px;
    align-items: center;
    margin-bottom: 12px;
    flex-wrap: wrap;
  }

  .api-method {
    font-size: 12px;
    font-weight: 700;
    padding: 3px 8px;
    border-radius: 4px;
    text-transform: uppercase;
    flex-shrink: 0;

    &.method-get {
      background: #ecfdf5;
      color: #00b96b;
    }

    &.method-post {
      background: #ebf5ff;
      color: #0066ff;
    }

    &.method-delete {
      background: #f3e8ff;
      color: #7c3aed;
    }
  }

  .api-endpoint {
    font-size: 15px;
    font-weight: 600;
    color: #111827;
    word-break: break-all;
  }

  .api-desc {
    font-size: 14px;
    color: #6b7280;
    margin: 0 0 12px;
  }

  .api-code-block {
    background: #1f2937;
    color: #00b96b;
    padding: 16px;
    border-radius: 8px;
    font-family: 'Fira Code', 'Consolas', monospace;
    font-size: 13px;
    overflow-x: auto;

    pre {
      margin: 0;
      white-space: pre-wrap;
      word-break: break-all;
    }
  }

  .api-cta {
    margin-top: 32px;
    padding: 24px;
    background: #fffbeb;
    border-radius: 12px;
    text-align: center;
  }

  .api-cta-title {
    font-size: 14px;
    font-weight: 600;
    margin: 8px 0 4px;
    color: #111827;
  }

  .api-cta-sub {
    font-size: 13px;
    color: #6b7280;
    margin: 0 0 12px;
  }

  .api-cta-btn {
    background: #0066ff;
    color: #fff;
    height: 40px;
    padding: 0 24px;
    border: none;
    border-radius: 10px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.3s ease;

    &:hover {
      background: #0044cc;
    }
  }

  // Doc Categories
  .doc-categories-section {
    max-width: 900px;
    margin: 64px auto 0;
  }

  .section-title {
    font-size: 24px;
    font-weight: 700;
    margin: 0 0 24px;
    text-align: center;
    color: #111827;
  }

  .doc-cat-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;

    @media (max-width: 768px) {
      grid-template-columns: repeat(2, 1fr);
    }

    @media (max-width: 480px) {
      grid-template-columns: 1fr;
    }
  }

  .doc-cat-card {
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    padding: 20px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 12px;
    transition: all 0.3s ease;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
      border-color: #0066ff;
    }
  }

  .doc-icon-wrapper {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .doc-name {
    font-weight: 600;
    font-size: 14px;
    margin: 0 0 2px;
    color: #111827;
  }

  .doc-count {
    font-size: 12px;
    color: #9ca3af;
    margin: 0;
  }

  // Help CTA Section
  .help-cta-section {
    max-width: 900px;
    margin: 64px auto 0;
    background: linear-gradient(135deg, #0066ff, #0044cc);
    border-radius: 20px;
    padding: 48px;
    text-align: center;
    color: #fff;
  }

  .cta-title {
    font-size: 28px;
    font-weight: 700;
    margin: 0 0 12px;
  }

  .cta-subtitle {
    font-size: 16px;
    opacity: 0.8;
    margin: 0 0 28px;
  }

  .cta-actions {
    display: flex;
    gap: 16px;
    justify-content: center;
    flex-wrap: wrap;
  }

  .cta-btn-primary {
    background: #fff;
    color: #0066ff;
    height: 48px;
    padding: 0 32px;
    border-radius: 10px;
    font-size: 15px;
    font-weight: 700;
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 6px;
    transition: all 0.3s ease;

    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    }
  }

  .cta-btn-outline {
    background: rgba(255, 255, 255, 0.15);
    color: #fff;
    height: 48px;
    padding: 0 32px;
    border-radius: 10px;
    font-size: 15px;
    font-weight: 700;
    border: 2px solid rgba(255, 255, 255, 0.3);
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 6px;
    transition: all 0.3s ease;

    &:hover {
      background: rgba(255, 255, 255, 0.25);
    }
  }

  .cta-contact {
    display: flex;
    justify-content: center;
    gap: 48px;
    margin-top: 32px;

    @media (max-width: 480px) {
      flex-direction: column;
      gap: 24px;
    }
  }

  .contact-item {
    text-align: center;
  }

  .contact-value {
    font-size: 18px;
    font-weight: 700;
    margin: 6px 0 4px;
  }

  .contact-label {
    font-size: 12px;
    opacity: 0.6;
    margin: 0;
  }
}
</style>
